// @flow

import _ from "lodash"
import fp from "lodash/fp"
import type { Map, List as ListType } from "immutable"
import { List } from "immutable"
import type Passenger from "@kiwicom/frontend-app/scripts/containers/mutable/Passenger"

import type { PassengerId } from "mmb/types/primitiveTypes"
import { getFileURIs } from "mmb/scenes/Booking/components/BookingFileUpload/BookingFileUploadHelpers"
// $FlowIssue
import phonePrefixes from "../../../../../../../../../data/countryPhonePrefixes.json" // eslint-disable-line import/no-restricted-paths
import {
  VIEWS,
  IS_VALID_WITHOUT_AFFECTED,
  STEPS_PATH,
  METHODS,
  STEP_DIRECTION,
} from "../consts/refundConsts"
import type { StepDirection } from "../consts/refundConsts"

const PATH_SEPARATOR = "."

export const getFlightLabelText = (
  flight: Object, // eslint-disable-line flowtype/no-weak-types
) => `${flight.departure.getIn(["where", "name"])} - ${flight.arrival.getIn(["where", "name"])}`

export const getPassengersNames = (passengers: Array<Passenger>, separator: string = ", ") =>
  passengers.map(passenger => passenger.getFullName()).join(separator)

// eslint-disable-next-line flowtype/no-weak-types
export const isActive = (affected: Map<string, boolean>, x: Object): boolean =>
  affected.get(x.id) || false

// View/Navigation helpers
// --
export const viewsList: ListType<string> = fp.reduce((acc, str) => acc.push(str), List.of())(VIEWS)

// all fields visited
// $FlowFixMe: error revealed by updating Flow and lodash libdef, please fix
export const allVisited = fp.compose(
  arr => !arr.length,
  fp.filter(field => !field.visited || !field.valid),
  fp.pick(["reason", "details"]),
)

// Validation helpers
// --

// additional validation of non-redux-form fields
export const generalFormIsValid = (
  reason: string,
  affectedFlights: Map<string, boolean>,
  affectedPassengers: Map<string, boolean>,
) =>
  IS_VALID_WITHOUT_AFFECTED[reason] ||
  (affectedFlights.valueSeq().some(val => val === true) &&
    affectedPassengers.valueSeq().some(val => val === true))

export const fieldIsFilled = (
  data: any, // eslint-disable-line flowtype/no-weak-types
) => (fp.isUndefined(data) ? __("refund.make_selection") : null) // currently this error is not displayed so does not need translation

export const getNextView = (arrayPath: Array<string>) => _.get(STEPS_PATH, arrayPath).next

export const getPreviousStep = (arrayPath: Array<string>) => {
  arrayPath.splice(-1, 1)
  if (_.get(STEPS_PATH, arrayPath).previous) {
    return {
      arrayPath: arrayPath.length > 1 ? arrayPath : [],
      view: _.get(STEPS_PATH, arrayPath).previous,
    }
  }
  return {
    arrayPath: arrayPath.length > 1 ? arrayPath : [],
    view: _.last(arrayPath),
  }
}

const pathToArray = (path: string) => path.split(PATH_SEPARATOR)
const arrayToPath = (array: Array<string>) => array.join(PATH_SEPARATOR)

export const getNewStep = (path: string, direction: StepDirection) => {
  const arrayPath = pathToArray(path)
  switch (direction) {
    case STEP_DIRECTION.NEXT: {
      const nextView = getNextView(arrayPath)
      arrayPath.push(nextView)
      return {
        path: arrayToPath(arrayPath),
        view: nextView,
      }
    }
    case STEP_DIRECTION.PREV: {
      const prevStep = getPreviousStep(arrayPath)
      return {
        path: arrayToPath(prevStep.arrayPath),
        view: prevStep.view,
      }
    }
    default: {
      return {
        path: "",
        view: VIEWS.REASON,
      }
    }
  }
}

export const formatAffectedFlights = (affectedFlights: { string: boolean }): Array<string> =>
  Object.keys(affectedFlights).filter(x => affectedFlights[x])

export const formatAffectedPassengers = (affectedPassengers: {
  number: boolean,
}): Array<PassengerId> =>
  Object.keys(affectedPassengers)
    .filter(x => affectedPassengers[x])
    .map(x => parseInt(x))

const constructFullPhoneNumber = (countryID, phoneNumber) =>
  `+${phonePrefixes[countryID]} ${phoneNumber}`

// eslint-disable-next-line flowtype/no-weak-types
export async function formatDataToSend(data: Object) {
  const {
    affectedFlights,
    affectedPassengers,
    bankHolder,
    bankIBAN,
    bankSwift,
    details,
    email,
    files,
    method,
    paymentEmail,
    paypalEmail,
    phone,
    phoneCountry,
    process, // undefined if not selected or string "true" | "false"
    reason,
  } = data

  const fileURIs = await getFileURIs(files)

  let methodData
  switch (method) {
    case METHODS.CREDITS:
      methodData = {
        payment_email: paymentEmail,
      }
      break
    case METHODS.PAYPAL:
      methodData = {
        payment_email: paypalEmail,
      }
      break
    case METHODS.BANK_ACCOUNT:
      methodData = {
        bank_holder: bankHolder,
        bank_IBAN: bankIBAN,
        bank_SWIFT: bankSwift,
      }
      break
    default:
      methodData = {}
  }

  return {
    affected_passenger_ids: formatAffectedPassengers(affectedPassengers),
    affected_segment_codes: formatAffectedFlights(affectedFlights),
    details,
    email,
    files: List.of(...fileURIs),
    refund_method: method,
    refund_method_data: methodData,
    phone: constructFullPhoneNumber(phoneCountry, phone),
    process: process === "false" ? "assessAmount" : "proceed",
    reason,
  }
}

// eslint-disable-next-line flowtype/no-weak-types
export function formatDataToSendAssisted(data: Object) {
  const {
    affectedFlights,
    affectedPassengers,
    bankHolder,
    bankIBAN,
    bankSwift,
    email,
    method,
    paymentEmail,
    paypalEmail,
    price,
    useBalance,
  } = data

  let methodData
  switch (method) {
    case METHODS.CREDITS:
      methodData = {
        paymentEmail,
      }
      break
    case METHODS.PAYPAL:
      methodData = {
        paymentEmail: paypalEmail,
      }
      break
    case METHODS.BANK_ACCOUNT:
      methodData = {
        bankHolder,
        bankIBAN,
        bankSwift,
      }
      break
  }

  return {
    passenger_ids: formatAffectedPassengers(affectedPassengers),
    segment_codes: formatAffectedFlights(affectedFlights),
    payment: {
      paid_by_card: {
        gate: "zooz",
        price: {
          currency: price.currency.toUpperCase(),
          amount: !useBalance ? String(price.amount) : "0",
        },
      },
      paid_by_credits: {
        price: {
          currency: price.currency.toUpperCase(),
          amount: useBalance ? String(price.amount) : "0",
        },
      },
    },
    refund_data: {
      email,
      method,
      ...methodData,
    },
  }
}
